interface Zone {
  id: string;
  name: string;
  status: string;
  color?: string;
}

interface MapLegendProps {
  zones: Zone[];
}

export function MapLegend({ zones }: MapLegendProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-4 overflow-hidden">
      {/* Header da Legenda */}
      <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-5 py-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Legenda do Mapa
        </h3>
      </div>

      <div className="p-5">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Legenda de Monitoramento */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-[#0085BD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800">Status de Monitoramento</h4>
            </div>

            <div className="space-y-3">
              {/* OK - Verde */}
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #22C55E 0%, #16A34A 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>15</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">0-15 dias sem relatório</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">OK</span>
                </div>
              </div>

              {/* Atenção - Amarelo */}
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #EAB308 0%, #CA8A04 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>25</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">16-30 dias sem relatório</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Atenção</span>
                </div>
              </div>

              {/* Alerta - Laranja */}
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #F97316 0%, #EA580C 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>40</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">31-45 dias sem relatório</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Alerta</span>
                </div>
              </div>

              {/* Crítico - Vermelho */}
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #EF4444 0%, #DC2626 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>60</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">+46 dias sem relatório</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Crítico</span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-2"></div>

              {/* Apenas entregas */}
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #9CA3AF 0%, #6B7280 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>—</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">Apenas entregas</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Sem monitoramento</span>
                </div>
              </div>

              {/* Emergência */}
              <div className="flex items-center gap-3 p-2 rounded-lg bg-red-50/50 border border-red-100">
                <div className="relative flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))' }}>
                  <div
                    className="w-7 h-7 flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      background: 'linear-gradient(145deg, #22C55E 0%, #16A34A 100%)',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      border: '2px solid rgba(255,255,255,0.9)'
                    }}
                  >
                    <span style={{ transform: 'rotate(45deg)' }}>!</span>
                  </div>
                  {/* Badge de emergência */}
                  <div
                    className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                      border: '1.5px solid white',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.4)'
                    }}
                  >
                    <span className="text-white text-[8px] font-bold">!</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">Cliente em emergência</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">⚠ Prioridade</span>
                </div>
              </div>
            </div>
          </div>

          {/* Legenda de Zonas */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800">Zonas de Atendimento</h4>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {zones
                .filter(zone => zone.status === "A")
                .map(zone => (
                  <div
                    key={zone.id}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: zone.color || "#0085BD",
                        boxShadow: `0 2px 4px ${zone.color || "#0085BD"}40`
                      }}
                    />
                    <span className="text-sm text-gray-700 truncate">{zone.name}</span>
                  </div>
                ))}
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 bg-gray-800"
                  style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                />
                <span className="text-sm text-gray-700">Sem zona</span>
              </div>
            </div>

            {/* Info adicional */}
            <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
              <div className="flex gap-2">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">Dica:</span> Clique em um pin para ver detalhes do cliente.
                  Use os filtros acima para localizar clientes específicos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
